/**
 * OrderImpactPanel Component
 * US-013D Phase 3: Display orders affected by production changes
 *
 * Features:
 * - Shows orders affected by bump/delay
 * - Colour coding: Green (on time), Amber (tight), Red (late)
 * - Customer name, due date, quantity, and days late
 * - Compact and full display modes
 */

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Package,
  User
} from 'lucide-react'
import { cn } from '@/lib/utils'

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get status configuration based on order impact
 */
function getOrderStatus(order) {
  if (order.days_late > 0) {
    return {
      status: 'late',
      color: 'text-red-700',
      bgColor: 'bg-red-50 border-red-200',
      badgeVariant: 'destructive',
      icon: AlertCircle,
      label: `${order.days_late} day${order.days_late !== 1 ? 's' : ''} late`
    }
  }

  if (order.is_at_risk) {
    return {
      status: 'at_risk',
      color: 'text-amber-700',
      bgColor: 'bg-amber-50 border-amber-200',
      badgeVariant: 'warning',
      icon: Clock,
      label: 'At risk'
    }
  }

  return {
    status: 'on_time',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50 border-emerald-200',
    badgeVariant: 'secondary',
    icon: CheckCircle2,
    label: 'On time'
  }
}

/**
 * Format date for display
 */
function formatDate(dateStr) {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short'
  })
}

// =============================================================================
// Sub-components
// =============================================================================

function OrderRow({ order, compact = false }) {
  const statusConfig = getOrderStatus(order)
  const StatusIcon = statusConfig.icon

  if (compact) {
    return (
      <div className={cn(
        'flex items-center justify-between py-2 px-3 rounded-lg border',
        statusConfig.bgColor
      )}>
        <div className="flex items-center gap-2 min-w-0">
          <StatusIcon className={cn('h-4 w-4 flex-shrink-0', statusConfig.color)} />
          <span className="font-medium text-sm truncate">{order.customer_name}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-muted-foreground">
            {order.quantity_kg}kg due {formatDate(order.due_date)}
          </span>
          <Badge variant={statusConfig.badgeVariant} className="text-xs">
            {statusConfig.label}
          </Badge>
        </div>
      </div>
    )
  }

  return (
    <div className={cn(
      'p-3 rounded-lg border',
      statusConfig.bgColor
    )}>
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <StatusIcon className={cn('h-4 w-4', statusConfig.color)} />
            <span className="font-medium">{order.customer_name}</span>
            <Badge variant={statusConfig.badgeVariant} className="text-xs">
              {statusConfig.label}
            </Badge>
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Package className="h-3 w-3" />
              {order.quantity_kg}kg
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Due: {formatDate(order.due_date)}
            </span>
          </div>
        </div>
      </div>

      {/* Delivery date comparison */}
      {order.original_delivery_date !== order.new_delivery_date && (
        <div className="mt-2 pt-2 border-t border-dashed text-sm">
          <span className="text-muted-foreground">Delivery: </span>
          <span className="line-through text-muted-foreground">
            {formatDate(order.original_delivery_date)}
          </span>
          <span className="mx-1 text-muted-foreground">→</span>
          <span className={cn('font-medium', statusConfig.color)}>
            {formatDate(order.new_delivery_date)}
          </span>
        </div>
      )}
    </div>
  )
}

function SummaryStats({ orders }) {
  const onTime = orders.filter(o => !o.is_at_risk && o.days_late === 0).length
  const atRisk = orders.filter(o => o.is_at_risk && o.days_late === 0).length
  const late = orders.filter(o => o.days_late > 0).length

  return (
    <div className="grid grid-cols-3 gap-2 mb-4">
      <div className="text-center p-2 rounded-lg bg-emerald-50 border border-emerald-200">
        <p className="text-lg font-bold text-emerald-700">{onTime}</p>
        <p className="text-xs text-emerald-600">On Time</p>
      </div>
      <div className="text-center p-2 rounded-lg bg-amber-50 border border-amber-200">
        <p className="text-lg font-bold text-amber-700">{atRisk}</p>
        <p className="text-xs text-amber-600">At Risk</p>
      </div>
      <div className="text-center p-2 rounded-lg bg-red-50 border border-red-200">
        <p className="text-lg font-bold text-red-700">{late}</p>
        <p className="text-xs text-red-600">Will Be Late</p>
      </div>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * OrderImpactPanel - Display orders affected by production changes
 *
 * @param {Object} props
 * @param {Array} props.orders - Array of affected order objects
 * @param {boolean} props.compact - Use compact display mode
 * @param {boolean} props.showSummary - Show summary statistics
 * @param {string} props.title - Optional custom title
 * @param {string} props.className - Additional CSS classes
 */
export default function OrderImpactPanel({
  orders = [],
  compact = false,
  showSummary = true,
  title = 'Order Impact',
  className
}) {
  if (!orders || orders.length === 0) {
    return (
      <Alert className="border-emerald-200 bg-emerald-50">
        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        <AlertDescription className="text-emerald-700">
          No customer orders affected by this change.
        </AlertDescription>
      </Alert>
    )
  }

  // Sort orders: late first, then at risk, then on time
  const sortedOrders = [...orders].sort((a, b) => {
    // Late orders first (by days late descending)
    if (a.days_late > 0 && b.days_late > 0) {
      return b.days_late - a.days_late
    }
    if (a.days_late > 0) return -1
    if (b.days_late > 0) return 1

    // Then at risk
    if (a.is_at_risk && !b.is_at_risk) return -1
    if (!a.is_at_risk && b.is_at_risk) return 1

    // Then by due date
    return new Date(a.due_date) - new Date(b.due_date)
  })

  const lateCount = orders.filter(o => o.days_late > 0).length
  const atRiskCount = orders.filter(o => o.is_at_risk && o.days_late === 0).length

  // Compact mode for embedding in other components
  if (compact) {
    return (
      <div className={cn('space-y-2', className)}>
        {lateCount > 0 && (
          <p className="text-sm text-red-600 font-medium">
            {lateCount} order{lateCount !== 1 ? 's' : ''} will be late
          </p>
        )}
        {atRiskCount > 0 && (
          <p className="text-sm text-amber-600 font-medium">
            {atRiskCount} order{atRiskCount !== 1 ? 's' : ''} at risk
          </p>
        )}
        <div className="space-y-1">
          {sortedOrders.slice(0, 5).map((order) => (
            <OrderRow key={order.order_id} order={order} compact />
          ))}
          {sortedOrders.length > 5 && (
            <p className="text-xs text-muted-foreground text-center py-1">
              +{sortedOrders.length - 5} more orders
            </p>
          )}
        </div>
      </div>
    )
  }

  // Full card display
  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <User className="h-4 w-4 text-primary" />
          {title}
        </CardTitle>
        <CardDescription>
          {orders.length} order{orders.length !== 1 ? 's' : ''} affected
        </CardDescription>
      </CardHeader>
      <CardContent>
        {showSummary && <SummaryStats orders={orders} />}

        <div className="space-y-2">
          {sortedOrders.map((order) => (
            <OrderRow key={order.order_id} order={order} />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
